import React from 'react';
import { X, CheckCircle2, AlertCircle, Info } from 'lucide-react';

type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface NotificationProps {
  type: NotificationType;
  message: React.ReactNode;
  onClose?: () => void;
  className?: string;
}

const Notification: React.FC<NotificationProps> = ({ 
  type = 'info', 
  message, 
  onClose,
  className = ''
}) => {
  // Convert message to string if it's not already a string or React node
  const formattedMessage = typeof message === 'object' && !React.isValidElement(message) 
    ? JSON.stringify(message) 
    : message;
  
  const bgColors = {
    success: 'bg-green-50',
    error: 'bg-red-50',
    info: 'bg-blue-50',
    warning: 'bg-yellow-50',
  };
  
  const textColors = {
    success: 'text-green-800',
    error: 'text-red-800',
    info: 'text-blue-800',
    warning: 'text-yellow-800',
  };
  
  const borderColors = {
    success: 'border-green-400',
    error: 'border-red-400',
    info: 'border-blue-400',
    warning: 'border-yellow-400',
  };
  
  const icons = {
    success: <CheckCircle2 className="h-5 w-5 text-green-500" />,
    error: <AlertCircle className="h-5 w-5 text-red-500" />,
    info: <Info className="h-5 w-5 text-blue-500" />,
    warning: <AlertCircle className="h-5 w-5 text-yellow-500" />,
  };
  
  return (
    <div 
      className={`rounded-md p-3 mb-4 flex items-start border ${bgColors[type]} ${borderColors[type]} ${className}`}
      role="alert"
    >
      <div className="flex-shrink-0 mr-3 pt-0.5">
        {icons[type]}
      </div>
      <div className={`flex-1 ${textColors[type]}`}>
        <p className="text-sm">{formattedMessage}</p>
      </div>
      {onClose && (
        <div className="ml-auto pl-3">
          <button
            type="button"
            className={`inline-flex rounded p-1.5 focus:outline-none focus:ring-2 ${textColors[type]}`}
            onClick={onClose}
          >
            <span className="sr-only">Dismiss</span>
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Notification; 